import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ActionBar,
	CheckIcon,
	DeleteIcon,
	Dialog,
	HierarchiesIcon,
	ModuleHeader,
	SpinnerWithBackground,
	StarIcon,
	Text,
} from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { HierarchyDetailsModal } from './components/HierarchyDetailsModal'
import { HierarchyUploadModal } from './components/HierarchyUploadModal'
import type { HierarchyFileItem, HierarchySearchInput } from './types'

const ScopeBadge = ({ scope }: { scope?: string }) => {
	if (scope === 'organization') {
		return (
			<span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700'>
				Organization
			</span>
		)
	}
	return (
		<span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600'>
			Cycle
		</span>
	)
}

export const HierarchiesPage = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const toast = useToast()
	const { data: organization, isLoading: isLoadingOrg } = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
	const [viewingHierarchy, setViewingHierarchy] = useState<HierarchyFileItem | null>(null)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

	const queryClient = useQueryClient()

	const { mutate: setDefaultHierarchy, isPending: isSettingDefault } = useMutation({
		...trpc.admin.organizations.edit.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Default hierarchy updated successfully')
			setSelectedIds(new Set())
			queryClient.invalidateQueries({
				queryKey: trpc.admin.organizations.getById.queryKey({ id: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
		onError: () => {
			toast.showToast('An error occurred while setting default hierarchy', 'error')
		},
	})

	const { mutate: deleteHierarchy } = useMutation({
		...trpc.admin.hierarchyFiles.deleteHierarchyFile.mutationOptions(),
		onSuccess: () => {
			setSelectedIds(new Set())
			toast.showToast('Hierarchies deleted successfully')
		},
		onError: () => {
			toast.showToast('An error occurred while deleting hierarchies', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.hierarchyFiles.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleDeleteHierarchies = async () => {
		const selectedHierarchyIds = Array.from(selectedIds)
		selectedHierarchyIds.forEach((id) => {
			deleteHierarchy({
				organizationId: organizationId as string,
				fileId: id,
				hierarchyName: '',
			})
		})
		setIsDeleteModalOpen(false)
	}

	const handleSetDefault = () => {
		const selectedId = Array.from(selectedIds)[0]
		if (!selectedId || !organizationId) return
		setDefaultHierarchy({ id: organizationId, defaultHierarchyFileId: selectedId })
	}

	const handleSelectionChange = (items: HierarchyFileItem[]) => {
		setSelectedIds(new Set(items.map((item) => item.id)))
	}

	const selectedHierarchiesCount = selectedIds.size
	const isDefault = (hierarchyId: string) => organization?.defaultHierarchyFileId === hierarchyId

	if (isLoadingOrg) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Organizations', to: '/organizations' },
					{ label: organization?.name || '', to: `/organizations/${organizationId}` },
					{ label: 'Hierarchies' },
				]}
				title='Hierarchies'
				logo={{
					url: organization?.logo || '',
					name: organization?.name || '',
				}}
				buttons={
					organization?.name
						? [
								{
									label: 'Upload Cycle Hierarchy',
									onClick: () => setIsUploadModalOpen(true),
									variant: 'primary',
								},
							]
						: undefined
				}
			/>
			<GenericTable<HierarchyFileItem, HierarchySearchInput>
				columns={[
					{
						field: 'name',
						label: 'Name',
						colSpan: 2,
						render: (hierarchy) => (
							<div className='flex items-center gap-2'>
								<Text
									variant='xsmall'
									weight='regular'
									className='group-hover:text-primary-600 cursor-pointer'
									onClick={(e) => {
										e.stopPropagation()
										setViewingHierarchy(hierarchy)
									}}>
									{hierarchy.name}
								</Text>
								<ScopeBadge scope={(hierarchy as HierarchyFileItem & { scope?: string }).scope} />
								{isDefault(hierarchy.id) && (
									<span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700'>
										<CheckIcon size={12} className='text-primary-700' />
										Default
									</span>
								)}
							</div>
						),
					},
					{
						field: 'createdAt',
						label: 'Created At',
						colSpan: 2,
						render: (hierarchy) => {
							const date = new Date(hierarchy.createdAt)
							const month = String(date.getMonth() + 1).padStart(2, '0')
							const day = String(date.getDate()).padStart(2, '0')
							const year = date.getFullYear()
							return (
								<Text variant='xsmall' weight='regular'>
									{`${month}-${day}-${year}`}
								</Text>
							)
						},
					},
				]}
				idField='id'
				selectable
				showPagination
				onSelectionChange={handleSelectionChange}
				queryOptionsGenerator={(options) => ({
					...trpc.admin.hierarchyFiles.search.queryOptions({
						...options,
						organizationId: organizationId as string,
					}),
				})}
				emptyState={{
					title: 'No hierarchies uploaded yet',
					description: 'Get started by uploading a hierarchy file',
					actionLabel: 'Upload hierarchy',
					icon: HierarchiesIcon,
				}}
			/>

			<HierarchyUploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} />

			{viewingHierarchy && (
				<HierarchyDetailsModal
					isOpen={true}
					onClose={() => setViewingHierarchy(null)}
					organizationId={organizationId as string}
					fileId={viewingHierarchy.id}
				/>
			)}

			{selectedHierarchiesCount > 0 && (
				<ActionBar
					leftActions={
						selectedHierarchiesCount === 1
							? [
									{
										variant: 'secondary',
										label: 'Set as default',
										onClick: handleSetDefault,
										disabled: isSettingDefault || isDefault(Array.from(selectedIds)[0]),
										isLoading: isSettingDefault,
										icon: StarIcon,
									},
								]
							: []
					}
					rightActions={[
						{
							variant: 'destructive',
							label: `Delete hierarchies (${selectedHierarchiesCount})`,
							onClick: () => setIsDeleteModalOpen(true),
							disabled: selectedHierarchiesCount === 0,
							icon: DeleteIcon,
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				onConfirm={handleDeleteHierarchies}
				title='Delete hierarchies'
				message={`Are you sure you want to delete ${selectedHierarchiesCount} ${selectedHierarchiesCount === 1 ? 'hierarchy' : 'hierarchies'}? This action cannot be reversed.`}
				type='delete'
				confirmLabel='Delete'
			/>
		</main>
	)
}
